import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  TextField,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Link,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { getActiveLinksByCountryRole } from "../../redux/actions/GetActiveLinksByCountryRoleAction";

const MyTools = () => {
  const dispatch = useDispatch();
  const activeLinksResponse = useSelector(
    (state) => state.activeLinksCountryRole.activeLinks
  );
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await dispatch(getActiveLinksByCountryRole());
      setIsLoading(false);
      setHasLoaded(true);
    };

    if (!hasLoaded) {
      fetchData();
    }
  }, [dispatch, hasLoaded]);

  // Transform the API data into the format required by the component
  const transformApiData = () => {
    if (!activeLinksResponse || activeLinksResponse.length === 0) {
      return { sharepoint: [], apps: [], reports: [] };
    }

    const transformed = {
      sharepoint: [],
      apps: [],
      reports: [],
    };

    activeLinksResponse.forEach((section) => {
      const sectionName = section.name.toLowerCase();

      // Map level2 to categories with their links
      const categories = section.level2.map((level2) => ({
        category: level2.name,
        links: level2.links.map((link) => ({
          name: link.linkName,
          url: link.url,
          idLink: link.idLink,
        })),
      }));

      // Assign to the corresponding section
      if (sectionName === "sharepoint") {
        transformed.sharepoint = categories;
      } else if (sectionName === "apps") {
        transformed.apps = categories;
      } else if (sectionName === "reports") {
        transformed.reports = categories;
      }
    });

    return transformed;
  };

  const data = transformApiData();

  const filterData = (items) => {
    if (!searchTerm) return items;

    return items
      .map((category) => ({
        ...category,
        links: category.links.filter(
          (link) =>
            link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.category.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      }))
      .filter((category) => category.links.length > 0);
  };

  const currentData = filterData(
    activeTab === 0
      ? data.sharepoint
      : activeTab === 1
      ? data.apps
      : data.reports
  );

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading links...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ flexGrow: 1, bgcolor: "background.default", minHeight: "100vh" }}
    >
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search links or categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
      </Paper>

      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              color: "text.primary",
              transition: "background-color 0.2s, color 0.2s",
              "&.Mui-selected": {
                color: "#fff !important",
                bgcolor: "#09092d",
              },
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          <Tab label="SharePoint" />
          <Tab label="Apps" />
          <Tab label="Reports" />
        </Tabs>
      </Paper>

      {currentData.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {searchTerm
              ? `No results found for "${searchTerm}"`
              : "No links available"}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {currentData.map((category, idx) => (
            <Card key={idx} elevation={1}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                    pb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                    {category.category}
                  </Typography>
                  <Chip
                    label={`${category.links.length} links`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Grid container spacing={2}>
                  {category.links.map((link, linkIdx) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={link.idLink || linkIdx}
                    >
                      <Link
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{ display: "block", height: "100%" }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "100%",
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "grey.50",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: 2,
                              bgcolor: "background.paper",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                            }}
                          >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="text.primary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {link.name}
                                </Typography>
                                <OpenInNewIcon
                                  sx={{
                                    fontSize: 14,
                                    color: "text.secondary",
                                  }}
                                />
                              </Box>
                              {link.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {link.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MyTools;
